/**
 * UC-O0 overdue invoice aging — data parsing (ported from src/utils/dataParser.js).
 */

export function parseConcatenatedJson(
	jsonString: string,
	_context = 'unknown'
): Record<string, unknown>[] {
	if (!jsonString || typeof jsonString !== 'string') {
		return []
	}

	try {
		const splitPatterns = [/\},\{/g, /\}\s*,\s*\{/g, /\}\s*\}\s*\{/g]
		let jsonObjects: string[] = []
		let foundPattern = false

		for (const pattern of splitPatterns) {
			const probe = new RegExp(pattern.source, pattern.flags)
			if (probe.test(jsonString)) {
				jsonObjects = jsonString.split(pattern)
				foundPattern = true
				break
			}
		}

		if (!foundPattern) {
			try {
				const singleObject = JSON.parse(jsonString) as unknown
				return Array.isArray(singleObject)
					? (singleObject as Record<string, unknown>[])
					: [singleObject as Record<string, unknown>]
			} catch {
				return []
			}
		}

		const parsedObjects: Record<string, unknown>[] = []
		jsonObjects.forEach((jsonStr, index) => {
			try {
				let completeJsonStr = jsonStr.trim()
				if (jsonObjects.length === 1) {
					// single
				} else if (index === 0) {
					completeJsonStr = `${completeJsonStr}}`
				} else if (index === jsonObjects.length - 1) {
					completeJsonStr = `{${completeJsonStr}`
				} else {
					completeJsonStr = `{${completeJsonStr}}`
				}
				const parsedObject = JSON.parse(completeJsonStr) as Record<string, unknown>
				if (parsedObject && typeof parsedObject === 'object') {
					parsedObjects.push(parsedObject)
				}
			} catch {
				// skip fragment
			}
		})

		return parsedObjects
	} catch {
		return []
	}
}

export interface AgingBucketParsed {
	range: string
	count: number
	amount: number
	uniqueCustomers?: number
	percentage?: number
}

export function parseAgingBucketBreakdown(
	agingBreakdown: unknown
): AgingBucketParsed[] {
	if (Array.isArray(agingBreakdown)) {
		return agingBreakdown.filter(
			(b): b is AgingBucketParsed =>
				b != null &&
				typeof b === 'object' &&
				typeof (b as AgingBucketParsed).range === 'string'
		) as AgingBucketParsed[]
	}
	if (!agingBreakdown || typeof agingBreakdown !== 'string') {
		return []
	}

	try {
		const buckets = agingBreakdown.split(' | ')
		return buckets
			.map(bucket => {
				const parts = bucket.split(':')
				if (parts.length >= 4) {
					const part3 = parseFloat(parts[3]) || 0
					const isIntegerCount = Number.isInteger(part3)
					return {
						range: parts[0].trim(),
						count: parseInt(parts[1], 10) || 0,
						amount: parseFloat(parts[2]) || 0,
						uniqueCustomers: isIntegerCount ? part3 : undefined,
						percentage: part3,
					} as AgingBucketParsed
				}
				return null
			})
			.filter((b): b is AgingBucketParsed => b !== null)
	} catch {
		return []
	}
}

export type MonthlyRowRaw = Record<string, unknown>

/** Parsed monthly row — standalone shape (no `Record` index) so `Omit` / aggregates stay typed. */
export interface ProcessedMonthRow {
	invoice_year: number
	invoice_month: number
	/** Present on some source rows / aggregates for display */
	month_label?: string
	half_year_number?: number | string
	invoice_month_start?: string
	period_start_date?: string
	total_deposit_amount?: number
	deposit_invoice_count?: number
	total_deposit_adjustment?: number
	deposit_adjustment_line_count?: number
	net_deposit_balance?: number
	active_clients: number
	month_total_invoices: number
	month_paid_invoices: number
	month_outstanding_invoices: number
	month_draft_invoices: number
	month_cancelled_invoices: number
	month_total_invoiced_amount: number
	month_total_paid_amount: number
	month_total_outstanding_amount: number
	month_overdue_invoices: number
	month_overdue_amount: number
	month_avg_invoice_size: number
	month_payment_rate: number
	overall_avg_days_overdue: number
	total_overdue_customers: number
	high_risk_customers: number
	customers_with_multiple_overdue: number
	month_collection_rate: number
	overdue_invoices: number
	overdue_amount: number
	outstanding_amount: number
	not_yet_due_amount: number
	overall_collection_risk: string
	overdue_percentage_of_outstanding: number
	period_collection_rate: number
	monthly_client_details: Record<string, unknown>[]
	clients_with_overdue_details: string
	top_multiple_overdue_customers: Record<string, unknown>[]
	top_overdue_invoices_detail: Record<string, unknown>[]
	aging_bucket_breakdown: AgingBucketParsed[]
}

export function processMonthlyData(data: unknown): ProcessedMonthRow[] {
	if (!Array.isArray(data)) {
		return []
	}

	return (data as MonthlyRowRaw[])
		.map((month, index) => {
			try {
				const processed: ProcessedMonthRow = {
					...month,
					invoice_year: parseInt(String(month.invoice_year), 10) || new Date().getFullYear(),
					invoice_month: parseInt(String(month.invoice_month), 10) || 1,
					active_clients: parseInt(String(month.active_clients), 10) || 0,
					month_total_invoices: parseFloat(String(month.month_total_invoices)) || 0,
					month_paid_invoices: parseFloat(String(month.month_paid_invoices)) || 0,
					month_outstanding_invoices: parseFloat(String(month.month_outstanding_invoices)) || 0,
					month_draft_invoices: parseFloat(String(month.month_draft_invoices)) || 0,
					month_cancelled_invoices: parseFloat(String(month.month_cancelled_invoices)) || 0,
					month_total_invoiced_amount: parseFloat(String(month.month_total_invoiced_amount)) || 0,
					month_total_paid_amount: parseFloat(String(month.month_total_paid_amount)) || 0,
					month_total_outstanding_amount:
						parseFloat(String(month.month_total_outstanding_amount)) ||
						parseFloat(String(month.outstanding_amount)) ||
						0,
					month_overdue_invoices:
						parseFloat(String(month.month_overdue_invoices)) ||
						parseFloat(String(month.overdue_invoices)) ||
						0,
					month_overdue_amount:
						parseFloat(String(month.month_overdue_amount)) ||
						parseFloat(String(month.overdue_amount)) ||
						0,
					month_avg_invoice_size: parseFloat(String(month.month_avg_invoice_size)) || 0,
					month_payment_rate: parseFloat(String(month.month_payment_rate)) || 0,
					overall_avg_days_overdue: parseFloat(String(month.overall_avg_days_overdue)) || 0,
					total_overdue_customers: parseInt(String(month.total_overdue_customers), 10) || 0,
					high_risk_customers: parseInt(String(month.high_risk_customers), 10) || 0,
					customers_with_multiple_overdue:
						parseInt(String(month.customers_with_multiple_overdue), 10) || 0,
					month_collection_rate:
						parseFloat(String(month.month_collection_rate)) ||
						parseFloat(String(month.period_collection_rate)) ||
						0,
					overdue_invoices: parseFloat(String(month.overdue_invoices)) || 0,
					overdue_amount: parseFloat(String(month.overdue_amount)) || 0,
					outstanding_amount: parseFloat(String(month.outstanding_amount)) || 0,
					not_yet_due_amount: parseFloat(String(month.not_yet_due_amount)) || 0,
					overall_collection_risk: String(month.overall_collection_risk || 'Low'),
					overdue_percentage_of_outstanding:
						parseFloat(String(month.overdue_percentage_of_outstanding)) || 0,
					period_collection_rate: parseFloat(String(month.period_collection_rate)) || 0,
					monthly_client_details: [],
					clients_with_overdue_details: '[]',
					top_multiple_overdue_customers: [],
					top_overdue_invoices_detail: [],
					aging_bucket_breakdown: [],
				}

				try {
					const clientDetailsString =
						month.clients_with_overdue_details ?? month.monthly_client_details

					if (typeof clientDetailsString === 'string') {
						try {
							const parsed = JSON.parse(clientDetailsString) as unknown
							processed.monthly_client_details = Array.isArray(parsed)
								? (parsed as Record<string, unknown>[])
								: [parsed as Record<string, unknown>]
							processed.clients_with_overdue_details = clientDetailsString
						} catch {
							processed.monthly_client_details = parseConcatenatedJson(
								clientDetailsString,
								`client_details_${index}`
							)
							processed.clients_with_overdue_details = JSON.stringify(
								processed.monthly_client_details
							)
						}
					} else if (Array.isArray(clientDetailsString)) {
						processed.monthly_client_details = clientDetailsString as Record<string, unknown>[]
						processed.clients_with_overdue_details = JSON.stringify(clientDetailsString)
					} else {
						processed.monthly_client_details = []
						processed.clients_with_overdue_details = '[]'
					}
					if (!Array.isArray(processed.monthly_client_details)) {
						processed.monthly_client_details = []
					}
				} catch {
					processed.monthly_client_details = []
					processed.clients_with_overdue_details = '[]'
				}

				try {
					const topCust = month.top_multiple_overdue_customers
					if (typeof topCust === 'string') {
						processed.top_multiple_overdue_customers = parseConcatenatedJson(
							topCust,
							`top_overdue_customers_${index}`
						)
					} else if (Array.isArray(topCust)) {
						processed.top_multiple_overdue_customers = topCust as Record<string, unknown>[]
					} else {
						processed.top_multiple_overdue_customers = []
					}
				} catch {
					processed.top_multiple_overdue_customers = []
				}

				try {
					const topInv = month.top_overdue_invoices_detail
					if (typeof topInv === 'string') {
						processed.top_overdue_invoices_detail = parseConcatenatedJson(
							topInv,
							`top_overdue_invoices_${index}`
						)
					} else if (Array.isArray(topInv)) {
						processed.top_overdue_invoices_detail = topInv as Record<string, unknown>[]
					} else {
						processed.top_overdue_invoices_detail = []
					}
				} catch {
					processed.top_overdue_invoices_detail = []
				}

				try {
					processed.aging_bucket_breakdown = parseAgingBucketBreakdown(month.aging_bucket_breakdown)
				} catch {
					processed.aging_bucket_breakdown = []
				}

				return processed
			} catch {
				return null
			}
		})
		.filter((m): m is ProcessedMonthRow => m !== null)
}

export interface DataStatistics {
	totalMonths: number
	totalClients: number
	totalOverdueAmount: number
	totalOverdueInvoices: number
	avgPaymentRate: number
	clientTiers: Record<string, number>
	dateRange: { earliest: Date | null; latest: Date | null }
	error?: string
}

export function getDataStatistics(data: unknown): DataStatistics {
	if (!Array.isArray(data)) {
		return {
			totalMonths: 0,
			totalClients: 0,
			totalOverdueAmount: 0,
			totalOverdueInvoices: 0,
			avgPaymentRate: 0,
			clientTiers: {},
			dateRange: { earliest: null, latest: null },
			error: 'Invalid data format',
		}
	}

	const stats: DataStatistics = {
		totalMonths: data.length,
		totalClients: 0,
		totalOverdueAmount: 0,
		totalOverdueInvoices: 0,
		avgPaymentRate: 0,
		clientTiers: {},
		dateRange: { earliest: null, latest: null },
	}

	let totalPaymentRate = 0
	let validMonths = 0

	for (const month of data as ProcessedMonthRow[]) {
		if (month.monthly_client_details) {
			stats.totalClients += month.monthly_client_details.length
			for (const client of month.monthly_client_details) {
				const tier = String(client.client_tier || 'Unknown')
				stats.clientTiers[tier] = (stats.clientTiers[tier] || 0) + 1
			}
		}

		stats.totalOverdueAmount += month.month_overdue_amount || 0
		stats.totalOverdueInvoices += month.month_overdue_invoices || 0

		if (month.month_payment_rate) {
			totalPaymentRate += month.month_payment_rate
			validMonths += 1
		}

		const start = month.invoice_month_start ?? month.period_start_date
		if (start) {
			const monthDate = new Date(String(start))
			if (!Number.isNaN(monthDate.getTime())) {
				if (!stats.dateRange.earliest || monthDate < stats.dateRange.earliest) {
					stats.dateRange.earliest = monthDate
				}
				if (!stats.dateRange.latest || monthDate > stats.dateRange.latest) {
					stats.dateRange.latest = monthDate
				}
			}
		}
	}

	stats.avgPaymentRate = validMonths > 0 ? totalPaymentRate / validMonths : 0
	return stats
}
